import React from 'react';
import {
  Avatar,
  Box,
  Container,
  IconButton,
  Rating,
  Typography,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageReviewItem = {
  name: LocalizedString;
  role?: LocalizedString;
  text: LocalizedString;
  rating?: number;
  avatar?: string;
};

export type PageReviewsSectionConfig = {
  enabled?: boolean;
  title?: LocalizedString;
  subtitle?: LocalizedString;
  autoplay?: boolean;
  intervalMs?: number;
  showArrows?: boolean;
  showDots?: boolean;
  variant?: 'glass' | 'soft';
  items?: PageReviewItem[];
};

type Props = {
  config?: PageReviewsSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageReviewsSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageReviewsSectionConfig = (config && typeof config === 'object') ? config : {};

  const { language } = useTranslation();

  const title = getLocalizedString(cfg.title, language, 'de', 'Was unsere Kunden sagen');
  const subtitle = getLocalizedString(cfg.subtitle, language, 'de', 'Echte Stimmen. Echte Erfahrungen. 100% Vertrauen.');
  const autoplay = cfg.autoplay !== false;
  const intervalMs = clamp(cfg.intervalMs, 1500, 20000) || 5500;
  const showArrows = cfg.showArrows !== false;
  const showDots = cfg.showDots !== false;
  const variant: 'glass' | 'soft' = cfg.variant === 'soft' ? 'soft' : 'glass';

  const items = Array.isArray(cfg.items)
    ? cfg.items
      .filter((it: any) => it && typeof it === 'object' && it.text)
      .map((it: any) => ({
        name: getLocalizedString(it.name, language, 'de', 'Kunde'),
        role: getLocalizedString(it.role, language, 'de', ''),
        text: getLocalizedString(it.text, language, 'de', ''),
        rating: clamp(it.rating, 0, 5) || 5,
        avatar: typeof it.avatar === 'string' ? it.avatar : '',
      }))
    : [];

  const [active, setActive] = React.useState(0);

  const resolve = React.useCallback((p?: string) => {
    if (!p) return '';
    return resolvePublicUrl ? resolvePublicUrl(p) : p;
  }, [resolvePublicUrl]);

  React.useEffect(() => {
    if (!autoplay) return;
    if (items.length <= 1) return;
    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [autoplay, intervalMs, items.length]);

  if (cfg.enabled === false) return null;

  const nav = (dir: -1 | 1) => {
    if (items.length <= 1) return;
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  const activeItem = items[active];

  const glassCardSx = {
    borderRadius: { xs: 4, md: 5 },
    border: '1px solid',
    borderColor: variant === 'soft' ? 'divider' : 'rgba(255,255,255,0.22)',
    bgcolor: variant === 'soft' ? 'background.paper' : 'rgba(255,255,255,0.10)',
    boxShadow: variant === 'soft' ? '0 16px 50px rgba(2,6,23,0.10)' : '0 26px 70px rgba(2,6,23,0.22)',
    backdropFilter: variant === 'soft' ? undefined : 'blur(10px)',
    p: { xs: 3, sm: 4, md: 5 },
  };

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.4 }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: 90, height: 4, borderRadius: 999, bgcolor: 'primary.main', opacity: 0.9 }} />
          </Box>
        </Box>

        {items.length === 0 ? (
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Typography variant="body2" color="text.secondary">Noch keine Reviews.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              borderRadius: { xs: 5, md: 6 },
              overflow: 'hidden',
              p: { xs: 2, sm: 3 },
              background:
                variant === 'soft'
                  ? 'radial-gradient(900px 420px at 10% 20%, rgba(59,130,246,0.08), transparent), radial-gradient(900px 420px at 90% 70%, rgba(99,102,241,0.08), transparent)'
                  : 'radial-gradient(900px 420px at 10% 20%, rgba(59,130,246,0.20), transparent), radial-gradient(900px 420px at 90% 70%, rgba(236,72,153,0.16), transparent)',
              border: '1px solid',
              borderColor: variant === 'soft' ? 'divider' : 'rgba(2,6,23,0.08)',
            }}
          >
            <Box sx={glassCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.25 }}>
                <Avatar
                  src={activeItem?.avatar ? resolve(activeItem.avatar) : undefined}
                  alt={activeItem?.name || 'Avatar'}
                  sx={{ width: 54, height: 54, border: '2px solid', borderColor: variant === 'soft' ? 'divider' : 'rgba(255,255,255,0.25)' }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                    {activeItem?.name || 'Kunde'}
                  </Typography>
                  {activeItem?.role ? (
                    <Typography variant="body2" color={variant === 'soft' ? 'text.secondary' : 'rgba(255,255,255,0.82)'}>
                      {activeItem.role}
                    </Typography>
                  ) : null}
                </Box>
                <Rating value={clamp(activeItem?.rating, 0, 5) || 5} precision={0.5} readOnly />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.75,
                  fontWeight: 700,
                  color: variant === 'soft' ? 'text.primary' : 'rgba(255,255,255,0.95)',
                }}
              >
                “{activeItem?.text}”
              </Typography>

              {showDots && items.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                  {items.map((_, i) => (
                    <Box
                      key={`dot-${i}`}
                      component="button"
                      type="button"
                      onClick={() => setActive(i)}
                      aria-label={`Review ${i + 1}`}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: i === active ? 'primary.main' : (variant === 'soft' ? 'divider' : 'rgba(255,255,255,0.35)'),
                        bgcolor: i === active ? 'primary.main' : 'transparent',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {showArrows && items.length > 1 && (
              <>
                <IconButton
                  onClick={() => nav(-1)}
                  sx={{
                    position: 'absolute',
                    left: { xs: 6, sm: 10 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: variant === 'soft' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.18)',
                    color: variant === 'soft' ? 'text.primary' : 'white',
                    border: '1px solid',
                    borderColor: variant === 'soft' ? 'divider' : 'rgba(255,255,255,0.20)',
                    '&:hover': { bgcolor: variant === 'soft' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.24)' },
                  }}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <IconButton
                  onClick={() => nav(1)}
                  sx={{
                    position: 'absolute',
                    right: { xs: 6, sm: 10 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: variant === 'soft' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.18)',
                    color: variant === 'soft' ? 'text.primary' : 'white',
                    border: '1px solid',
                    borderColor: variant === 'soft' ? 'divider' : 'rgba(255,255,255,0.20)',
                    '&:hover': { bgcolor: variant === 'soft' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.24)' },
                  }}
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PageReviewsSection;
